import React from 'react';

import { Button } from '../../components/ui/button';

import { Input } from '../../components/ui/input';
import { DatePickerFr } from '../../components/ui/DatePickerFr';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertCircle, FileText } from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';

import CountryPhoneSelect from '../../components/CountryPhoneSelect';
import AddressAutocomplete from '../../components/AddressAutocomplete';

import '../ProcesVerbaux.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _API = `${BACKEND_URL}/api`;

export default function ProcesVerbauxDetailDialog({ ctx }) {
  const { dialogKey, dialogOpen, formData, handleInputChange, handleSubmitPv, setDialogKey, setDialogOpen, setFormData, souscriptions } = ctx;
  return (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (open) setDialogKey(prev => prev + 1);
        }}>
          <DialogTrigger asChild>
            <Button>
              <FileText className="h-4 w-4 mr-2" />
              Créer un PV
            </Button>
          </DialogTrigger>
          <DialogContent key={`pv-dialog-${dialogKey}`} className="max-w-7xl w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Créer un nouveau Procès-Verbal</DialogTitle>
              <DialogDescription>
                Remplissez les informations du chantier. Vous pourrez déposer le document ensuite.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitPv} className="space-y-6">
              {/* Section principale - 2 colonnes */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="souscription_id">Dispositif solidaire associé *</Label>
                  <Select
                    value={formData.souscription_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, souscription_id: value }))}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez un dispositif" />
                    </SelectTrigger>
                    <SelectContent>
                      {souscriptions.filter(s => s.id && s.id.trim() !== '').map(sub => (
                        <SelectItem key={sub.id} value={sub.id}>
                          {sub.numero_police} - {sub.dispositif?.nom || 'N/A'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="nom_chantier">Désignation du chantier *</Label>
                  <Input
                    id="nom_chantier"
                    name="nom_chantier"
                    value={formData.nom_chantier}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* Section Maître d'ouvrage */}
              <div key={`mo-section-${formData.type_maitre_ouvrage}`} className="border-t pt-4 mt-4">
                <h3 className="font-semibold text-lg mb-3">Maître d'ouvrage</h3>
                
                <div className="mb-4">
                  <Label className="mb-2 block">Type de maître d'ouvrage *</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="type_maitre_ouvrage"
                        value="particulier"
                        checked={formData.type_maitre_ouvrage === 'particulier'}
                        onChange={handleInputChange}
                        required
                        className="w-4 h-4"
                      />
                      <span>Particulier</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="type_maitre_ouvrage"
                        value="professionnel"
                        checked={formData.type_maitre_ouvrage === 'professionnel'}
                        onChange={handleInputChange}
                        required
                        className="w-4 h-4"
                      />
                      <span>Professionnel</span>
                    </label>
                  </div>
                </div>

                {formData.type_maitre_ouvrage === 'particulier' && (
                  <div 
                    key="particulier-fields" 
                    className="space-y-3"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="mo_particulier_nom">Nom *</Label>
                        <Input
                          id="mo_particulier_nom"
                          name="mo_particulier_nom"
                          value={formData.mo_particulier_nom}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="mo_particulier_prenom">Prénom *</Label>
                        <Input
                          id="mo_particulier_prenom"
                          name="mo_particulier_prenom"
                          value={formData.mo_particulier_prenom}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <AddressAutocomplete
                        label="Adresse postale"
                        placeholder="Commencez à taper une adresse..."
                        required={true}
                        initialValue={formData.mo_particulier_adresse}
                        addressFieldName="mo_particulier_adresse"
                        postalCodeFieldName="mo_particulier_code_postal"
                        cityFieldName="mo_particulier_ville"
                        countryFieldName="mo_particulier_pays"
                        onAddressSelect={(addressData) => {
                          setFormData(prev => ({
                            ...prev,
                            mo_particulier_adresse: addressData.mo_particulier_adresse || '',
                            mo_particulier_code_postal: addressData.mo_particulier_code_postal || '',
                            mo_particulier_ville: addressData.mo_particulier_ville || '',
                            mo_particulier_pays: addressData.mo_particulier_pays || 'France'
                          }));
                        }}
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="mo_particulier_code_postal">Code postal *</Label>
                        <Input
                          id="mo_particulier_code_postal"
                          name="mo_particulier_code_postal"
                          value={formData.mo_particulier_code_postal}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="mo_particulier_ville">Ville *</Label>
                        <Input
                          id="mo_particulier_ville"
                          name="mo_particulier_ville"
                          value={formData.mo_particulier_ville}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="mo_particulier_pays">Pays *</Label>
                        <Input
                          id="mo_particulier_pays"
                          name="mo_particulier_pays"
                          value={formData.mo_particulier_pays}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <CountryPhoneSelect
                          label="Téléphone"
                          required={true}
                          phoneValue={formData.mo_particulier_telephone}
                          countryCode="FR"
                          onPhoneChange={(phone) => {
                            setFormData(prev => ({
                              ...prev,
                              mo_particulier_telephone: phone
                            }));
                          }}
                          onCountryChange={(_country) => {
                          }}
                        />
                      </div>
                      <div>
                        <Label htmlFor="mo_particulier_email">Email *</Label>
                        <Input
                          id="mo_particulier_email"
                          name="mo_particulier_email"
                          type="email"
                          value={formData.mo_particulier_email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="border-t pt-3 mt-3">
                      <h4 className="font-medium mb-2">Adresse de l'opération</h4>
                      <div>
                        <AddressAutocomplete
                          label="Adresse"
                          placeholder="Commencez à taper l'adresse du chantier..."
                          required={true}
                          initialValue={formData.operation_adresse}
                          addressFieldName="operation_adresse"
                          postalCodeFieldName="operation_code_postal"
                          cityFieldName="operation_ville"
                          countryFieldName="operation_pays"
                          onAddressSelect={(addressData) => {
                            setFormData(prev => ({
                              ...prev,
                              operation_adresse: addressData.operation_adresse || '',
                              operation_code_postal: addressData.operation_code_postal || '',
                              operation_ville: addressData.operation_ville || '',
                              operation_pays: addressData.operation_pays || 'France'
                            }));
                          }}
                        />
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3 mt-2">
                        <div>
                          <Label htmlFor="operation_code_postal">Code postal *</Label>
                          <Input
                            id="operation_code_postal"
                            name="operation_code_postal"
                            value={formData.operation_code_postal}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="operation_ville">Ville *</Label>
                          <Input
                            id="operation_ville"
                            name="operation_ville"
                            value={formData.operation_ville}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="operation_pays">Pays *</Label>
                          <Input
                            id="operation_pays"
                            name="operation_pays"
                            value={formData.operation_pays}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {formData.type_maitre_ouvrage === 'professionnel' && (
                  <div 
                    key="professionnel-fields" 
                    className="space-y-3"
                  >
                    <div>
                      <Label htmlFor="mo_pro_raison_sociale">Raison sociale *</Label>
                      <Input
                        id="mo_pro_raison_sociale"
                        name="mo_pro_raison_sociale"
                        value={formData.mo_pro_raison_sociale}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="mo_pro_siret">SIRET *</Label>
                      <Input
                        id="mo_pro_siret"
                        name="mo_pro_siret"
                        value={formData.mo_pro_siret}
                        onChange={handleInputChange}
                        placeholder="14 chiffres"
                        maxLength={14}
                        required
                      />
                    </div>
                    
                    <div>
                      <AddressAutocomplete
                        label="Adresse postale"
                        placeholder="Commencez à taper l'adresse de l'entreprise..."
                        required={true}
                        initialValue={formData.mo_pro_adresse}
                        addressFieldName="mo_pro_adresse"
                        postalCodeFieldName="mo_pro_code_postal"
                        cityFieldName="mo_pro_ville"
                        countryFieldName="mo_pro_pays"
                        onAddressSelect={(addressData) => {
                          setFormData(prev => ({
                            ...prev,
                            mo_pro_adresse: addressData.mo_pro_adresse || '',
                            mo_pro_code_postal: addressData.mo_pro_code_postal || '',
                            mo_pro_ville: addressData.mo_pro_ville || '',
                            mo_pro_pays: addressData.mo_pro_pays || 'France'
                          }));
                        }}
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="mo_pro_code_postal">Code postal *</Label>
                        <Input
                          id="mo_pro_code_postal"
                          name="mo_pro_code_postal"
                          value={formData.mo_pro_code_postal}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="mo_pro_ville">Ville *</Label>
                        <Input
                          id="mo_pro_ville"
                          name="mo_pro_ville"
                          value={formData.mo_pro_ville}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="mo_pro_pays">Pays *</Label>
                        <Input
                          id="mo_pro_pays"
                          name="mo_pro_pays"
                          value={formData.mo_pro_pays}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="mo_pro_personne_contact">Personne contact *</Label>
                      <Input
                        id="mo_pro_personne_contact"
                        name="mo_pro_personne_contact"
                        value={formData.mo_pro_personne_contact}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <CountryPhoneSelect
                          label="Téléphone"
                          required={true}
                          phoneValue={formData.mo_pro_telephone}
                          countryCode="FR"
                          onPhoneChange={(phone) => {
                            setFormData(prev => ({
                              ...prev,
                              mo_pro_telephone: phone
                            }));
                          }}
                          onCountryChange={(_country) => {
                          }}
                        />
                      </div>
                      <div>
                        <Label htmlFor="mo_pro_email">Email *</Label>
                        <Input
                          id="mo_pro_email"
                          name="mo_pro_email"
                          type="email"
                          value={formData.mo_pro_email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type_operation">Type d'opération *</Label>
                  <Select
                    value={formData.type_operation}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, type_operation: value }))}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neuf">Neuf</SelectItem>
                      <SelectItem value="renovation">Rénovation</SelectItem>
                      <SelectItem value="extension">Extension</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="type_intervention">Type d'intervention *</Label>
                  <Select
                    value={formData.type_intervention}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, type_intervention: value }))}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct">Direct</SelectItem>
                      <SelectItem value="sous_traitant">Sous-traitant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="cout_ht_intervention">Coût (Montant de base) de l'intervention (€) *</Label>
                <Input
                  id="cout_ht_intervention"
                  name="cout_ht_intervention"
                  type="number"
                  step="0.01"
                  value={formData.cout_ht_intervention}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="date_debut_travaux">Date de début des travaux *</Label>
                  <DatePickerFr
                    id="date_debut_travaux"
                    className="w-full"
                    value={formData.date_debut_travaux}
                    onChange={(v) => handleInputChange({ target: { name: 'date_debut_travaux', value: v } })}
                  />
                </div>

                <div>
                  <Label htmlFor="date_fin_travaux">Date de livraison *</Label>
                  <DatePickerFr
                    id="date_fin_travaux"
                    className="w-full"
                    value={formData.date_fin_travaux}
                    onChange={(v) => handleInputChange({ target: { name: 'date_fin_travaux', value: v } })}
                  />
                </div>

                <div>
                  <Label htmlFor="date_reception">Date de réception *</Label>
                  <DatePickerFr
                    id="date_reception"
                    className="w-full"
                    value={formData.date_reception}
                    onChange={(v) => handleInputChange({ target: { name: 'date_reception', value: v } })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="statut_reserves">Statut *</Label>
                <Select
                  value={formData.statut_reserves}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, statut_reserves: value }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sans_reserves">Sans réserves</SelectItem>
                    <SelectItem value="avec_reserves">Avec réserves</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Vous avez 30 jours à partir de la date de réception pour déposer le PV.
                  Dépôt dans les temps : remise de 3,50€. Retard : pénalité de 1€/jour.
                </AlertDescription>
              </Alert>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1">Créer le PV</Button>
                <Button type="button" onClick={() => setDialogOpen(false)} variant="outline" className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
  );
}
